import { useEffect, useState } from "react";
import { TgScreen } from "../components/TgScreen";
import { Card } from "../components/atoms";
import {
  recentContacts,
  rememberContact,
  forgetContact,
  renameContact,
} from "../lib/contacts-store";
import {
  resolveTelecryptUsername,
  getChatSavedAddresses,
  deleteChatSavedAddress,
  updateChatSavedLabel,
  TeleswapBotError,
  type ChatSavedAddress,
} from "../lib/teleswap-client";
import { truncateAddr } from "../lib/format";
import { haptic } from "../lib/haptics";

interface Props {
  onPickRecipient: (args: { unifiedAddress: string; username: string | null }) => void;
}

export function ContactsScreen({ onPickRecipient }: Props) {
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState(() => recentContacts());
  const [chatSaved, setChatSaved] = useState<ChatSavedAddress[]>([]);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline-edit state — keyed by UA so only one row is in edit mode at
  // a time. `editingValue` is the draft text; we commit on Enter or
  // check-icon, abandon on Escape or blur (with no save).
  const [editingUa, setEditingUa] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  // Pull addresses the bot has saved from chat DMs. Re-fetch when the
  // tab is re-focused so a UA pasted into the bot chat shows up the
  // moment the user comes back to the wallet.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const list = await getChatSavedAddresses();
      if (!cancelled) setChatSaved(list);
    };
    void load();
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const onSearch = async () => {
    const handle = query.trim().replace(/^@/, "");
    if (!handle) return;
    setResolving(true);
    setError(null);
    try {
      const addr = await resolveTelecryptUsername(handle);
      rememberContact({
        username: addr.tg_username ?? handle,
        unifiedAddress: addr.unified_address,
      });
      setRecents(recentContacts());
      setQuery("");
      haptic("success");
      onPickRecipient({
        unifiedAddress: addr.unified_address,
        username: addr.tg_username ?? handle,
      });
    } catch (e) {
      if (e instanceof TeleswapBotError && e.status === 404) {
        haptic("warning");
        setError(`@${handle} hasn't joined Shieldz yet.`);
      } else {
        haptic("error");
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setResolving(false);
    }
  };

  const removeContact = (ua: string) => {
    forgetContact(ua);
    setRecents(recentContacts());
  };

  const removeChatSaved = async (ua: string) => {
    setChatSaved((prev) => prev.filter((a) => a.unified_address !== ua));
    await deleteChatSavedAddress(ua);
  };

  const startEdit = (ua: string, current: string) => {
    haptic("light");
    setEditingUa(ua);
    setEditingValue(current);
  };

  const cancelEdit = () => {
    setEditingUa(null);
    setEditingValue("");
  };

  const commitRecentEdit = (ua: string) => {
    const next = editingValue.trim();
    if (!next) {
      cancelEdit();
      return;
    }
    renameContact(ua, next);
    setRecents(recentContacts());
    haptic("success");
    cancelEdit();
  };

  const commitChatSavedEdit = (ua: string) => {
    const next = editingValue.trim();
    if (!next) {
      cancelEdit();
      return;
    }
    // Optimistic local update — bot PUT is fire-and-forget.
    setChatSaved((prev) =>
      prev.map((a) =>
        a.unified_address === ua ? { ...a, label: next } : a,
      ),
    );
    void updateChatSavedLabel(ua, next);
    haptic("success");
    cancelEdit();
  };

  // Hide chat-saved entries that already have a richer recent-contact
  // record (recents have a @username, chat-saved often don't).
  const recentUaSet = new Set(recents.map((r) => r.unifiedAddress));
  const chatOnly = chatSaved.filter(
    (a) => !recentUaSet.has(a.unified_address),
  );

  return (
    <TgScreen title="Contacts" bottomNav="contacts">
      <div style={{ padding: "12px 16px 24px" }}>
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "var(--hint)",
              marginBottom: 6,
            }}
          >
            Send to @username
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void onSearch();
              }}
              placeholder="@alice"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 15,
                fontFamily: "var(--font-text)",
                color: "var(--text)",
                padding: 0,
              }}
            />
            <button
              onClick={onSearch}
              disabled={resolving || !query.trim()}
              style={{
                padding: "6px 14px",
                background: "var(--tg-theme-button-color, #2c8acb)",
                color: "var(--tg-theme-button-text-color, white)",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: resolving ? "wait" : "pointer",
                opacity: resolving || !query.trim() ? 0.6 : 1,
              }}
            >
              {resolving ? "…" : "Find"}
            </button>
          </div>
          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--destructive, #d33)" }}>
              {error}
            </div>
          )}
        </Card>

        {chatOnly.length > 0 && (
          <>
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "var(--hint)",
                margin: "16px 4px 8px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>From the bot chat ({chatOnly.length})</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
              {chatOnly.map((c) => {
                const isEditing = editingUa === c.unified_address;
                return (
                  <Card key={c.unified_address} style={{ padding: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {isEditing ? (
                          <input
                            autoFocus
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitChatSavedEdit(c.unified_address);
                              else if (e.key === "Escape") cancelEdit();
                            }}
                            placeholder="Label"
                            style={{
                              width: "100%",
                              background: "var(--bg-3)",
                              border: "none",
                              outline: "none",
                              fontSize: 13,
                              fontWeight: 600,
                              fontFamily: "var(--font-text)",
                              color: "var(--text)",
                              padding: "4px 8px",
                              borderRadius: 6,
                            }}
                          />
                        ) : (
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-soft)" }}>
                            {c.label ?? "Pasted in chat"}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 11,
                            fontFamily: "monospace",
                            color: "var(--hint)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginTop: isEditing ? 4 : 0,
                          }}
                        >
                          {truncateAddr(c.unified_address)}
                        </div>
                      </div>
                      {isEditing ? (
                        <button
                          onClick={() => commitChatSavedEdit(c.unified_address)}
                          aria-label="Save label"
                          style={{
                            padding: "6px 10px",
                            background: "var(--tg-theme-button-color, #2c8acb)",
                            color: "var(--tg-theme-button-text-color, white)",
                            border: "none",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          ✓
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            haptic("medium");
                            onPickRecipient({
                              unifiedAddress: c.unified_address,
                              username: null,
                            });
                          }}
                          style={{
                            padding: "6px 12px",
                            background: "var(--tg-theme-button-color, #2c8acb)",
                            color: "var(--tg-theme-button-text-color, white)",
                            border: "none",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Send
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          onClick={() => startEdit(c.unified_address, c.label ?? "")}
                          aria-label="Edit label"
                          style={{
                            padding: "6px 8px",
                            background: "transparent",
                            color: "var(--hint)",
                            border: "none",
                            fontSize: 14,
                            cursor: "pointer",
                          }}
                        >
                          ✏
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (isEditing) {
                            cancelEdit();
                            return;
                          }
                          haptic("light");
                          void removeChatSaved(c.unified_address);
                        }}
                        aria-label={isEditing ? "Cancel edit" : "Remove"}
                        style={{
                          padding: "6px 8px",
                          background: "transparent",
                          color: "var(--hint)",
                          border: "none",
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "var(--hint)",
            margin: "16px 4px 8px",
          }}
        >
          Recent ({recents.length})
        </div>

        {recents.length === 0 ? (
          <div
            style={{
              padding: "32px 12px",
              textAlign: "center",
              color: "var(--hint)",
              fontSize: 13,
            }}
          >
            No contacts yet. Search for a @username above to add the first one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {recents.map((c) => {
              const isEditing = editingUa === c.unifiedAddress;
              return (
                <Card key={c.unifiedAddress} style={{ padding: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {isEditing ? (
                        <input
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRecentEdit(c.unifiedAddress);
                            else if (e.key === "Escape") cancelEdit();
                          }}
                          placeholder="Label"
                          style={{
                            width: "100%",
                            background: "var(--bg-3)",
                            border: "none",
                            outline: "none",
                            fontSize: 15,
                            fontWeight: 600,
                            fontFamily: "var(--font-text)",
                            color: "var(--text)",
                            padding: "4px 8px",
                            borderRadius: 6,
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: 15, fontWeight: 600 }}>
                          {c.username.startsWith("@") || c.username.includes(" ")
                            ? c.username
                            : `@${c.username}`}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          fontFamily: "monospace",
                          color: "var(--hint)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: isEditing ? 4 : 0,
                        }}
                      >
                        {truncateAddr(c.unifiedAddress)}
                      </div>
                    </div>
                    {isEditing ? (
                      <button
                        onClick={() => commitRecentEdit(c.unifiedAddress)}
                        aria-label="Save label"
                        style={{
                          padding: "6px 10px",
                          background: "var(--tg-theme-button-color, #2c8acb)",
                          color: "var(--tg-theme-button-text-color, white)",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        ✓
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          haptic("medium");
                          onPickRecipient({
                            unifiedAddress: c.unifiedAddress,
                            username: c.username,
                          });
                        }}
                        style={{
                          padding: "6px 12px",
                          background: "var(--tg-theme-button-color, #2c8acb)",
                          color: "var(--tg-theme-button-text-color, white)",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Send
                      </button>
                    )}
                    {!isEditing && (
                      <button
                        onClick={() => startEdit(c.unifiedAddress, c.username)}
                        aria-label="Edit contact label"
                        style={{
                          padding: "6px 8px",
                          background: "transparent",
                          color: "var(--hint)",
                          border: "none",
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                      >
                        ✏
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (isEditing) {
                          cancelEdit();
                          return;
                        }
                        haptic("light");
                        removeContact(c.unifiedAddress);
                      }}
                      aria-label={isEditing ? "Cancel edit" : "Remove contact"}
                      style={{
                        padding: "6px 8px",
                        background: "transparent",
                        color: "var(--hint)",
                        border: "none",
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </TgScreen>
  );
}
